import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { OportunidadesCard } from '../components/OportunidadesCard';
import { AgendaHojeCard } from '../components/AgendaHojeCard';
import { MinhaMetaCard } from '../components/MinhaMetaCard';
import { KbNewsBanner } from '../components/KbNewsBanner';
import { DivergentContractsCard } from '../components/DivergentContractsCard';
import { UpcomingEventsWidget } from '../components/UpcomingEventsWidget';
import { ChamadasHojeCard } from '../components/ChamadasHojeCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users,
  UserCircle,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Clock,
  Zap,
  Flame,
  Sun,
  Plug,
  Shield,
  Tv,
  AlertTriangle,
  XCircle,
  FileText,
  Loader2,
  BatteryCharging,
  BarChart3,
  ArrowRight,
  DollarSign,
  Wallet,
  CalendarClock,
  Activity,
  Phone,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_LABELS = {
  pendente: 'Pendente',
  em_processo: 'Em Processo',
  concluido: 'Concluído',
  anulado: 'Anulado',
  agendado: 'Agendado',
};

const CATEGORY_CONFIG = {
  luz_gas: { label: 'Energia', icon: Zap, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  solar: { label: 'Solar', icon: Sun, color: 'text-amber-500', bg: 'bg-amber-50' },
  telecomunicacoes: { label: 'Telecomunicações', icon: Tv, color: 'text-purple-500', bg: 'bg-purple-50' },
  carregamento: { label: 'Carregamento', icon: BatteryCharging, color: 'text-green-500', bg: 'bg-green-50' },
  alarmes: { label: 'Alarmes', icon: Shield, color: 'text-red-500', bg: 'bg-red-50' },
};

export const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [urgentFollowUps, setUrgentFollowUps] = useState([]);
  const [leadsReminder, setLeadsReminder] = useState({ total: 0, atrasadas: 0, hoje: 0, amanha: 0 });
  const [owner, setOwner] = useState({ partner_id: '', comercial_id: '' });
  const [partners, setPartners] = useState([]);
  const [comerciais, setComerciais] = useState([]);

  const isFinanceRole = ['super_admin', 'gestor'].includes(user?.role);
  const canFilterOwner = ['super_admin', 'gestor', 'admin'].includes(user?.role);

  useEffect(() => {
    if (!canFilterOwner) return;
    axios.get(`${API_URL}/leads/assignable-partners`).then(r => setPartners(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/leads/assignable-users`).then(r => setComerciais(r.data || [])).catch(() => {});
  }, [canFilterOwner]);

  const fetchDashboard = React.useCallback(async () => {
    try {
      const dashParams = {};
      if (owner.comercial_id) dashParams.comercial_id = owner.comercial_id;
      else if (owner.partner_id) dashParams.partner_id = owner.partner_id;
      const promises = [
        axios.get(`${API_URL}/dashboard`, { params: dashParams }),
        axios.get(`${API_URL}/follow-ups`, { params: { status: 'pendente' } }),
        axios.get(`${API_URL}/leads/reminder-count`).catch(() => ({ data: null })),
      ];
      if (isFinanceRole) {
        promises.push(axios.get(`${API_URL}/dashboard/finance-summary`));
      }
      const results = await Promise.all(promises);
      setStats(results[0].data);
      const fus = (results[1].data?.follow_ups || []).slice().sort((a, b) => {
        const aRed = a.is_red ? 0 : a.is_due ? 1 : 2;
        const bRed = b.is_red ? 0 : b.is_due ? 1 : 2;
        if (aRed !== bRed) return aRed - bRed;
        return (a.scheduled_at || '').localeCompare(b.scheduled_at || '');
      }).slice(0, 5);
      setUrgentFollowUps(fus);
      if (results[2]?.data) setLeadsReminder(results[2].data);
      if (results[3]) setFinanceSummary(results[3].data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isFinanceRole, owner]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useRefreshListener(fetchDashboard);

  // Refresh follow-ups + alerts when user returns to the tab/window.
  // Covers: clicking back from /leads/follow-ups-fatura, switching tabs, etc.
  useEffect(() => {
    const onFocus = () => { fetchDashboard(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchDashboard(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchDashboard]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-[#2D3E6F]" />
        </div>
      </Layout>
    );
  }

  if (!stats) return <Layout><p className="text-center py-20 text-slate-500">Erro ao carregar dashboard</p></Layout>;

  const { total_contracts, contracts_by_status, contracts_by_category, contracts_by_company, energy_by_type, telecom_by_type, solar_stats, alerts, recent_activities, my_tickets, team_members } = stats;

  const concluidos = contracts_by_status?.concluido || 0;
  const emProcesso = contracts_by_status?.em_processo || 0;
  const pendentes = contracts_by_status?.pendente || 0;
  const anulados = contracts_by_status?.anulado || 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              Olá, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {owner.comercial_id
                ? `A ver: ${comerciais.find(c => c.id === owner.comercial_id)?.name || 'comercial'}`
                : owner.partner_id
                  ? `A ver: ${partners.find(p => p.id === owner.partner_id)?.name || 'parceiro'}`
                  : 'Resumo da sua atividade'}
            </p>
          </div>
          {canFilterOwner && (
            <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-owner-filter">
              <Select value={owner.partner_id || '__all__'} onValueChange={(v) => setOwner({ partner_id: v === '__all__' ? '' : v, comercial_id: '' })}>
                <SelectTrigger className="w-[180px] h-9 bg-white" data-testid="owner-partner-select"><SelectValue placeholder="Todos os parceiros" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os parceiros</SelectItem>
                  {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={owner.comercial_id || '__all__'} onValueChange={(v) => setOwner(o => ({ ...o, comercial_id: v === '__all__' ? '' : v }))}>
                <SelectTrigger className="w-[180px] h-9 bg-white" data-testid="owner-comercial-select"><SelectValue placeholder="Todos os comerciais" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os comerciais</SelectItem>
                  {(owner.partner_id ? comerciais.filter(c => c.partner_id === owner.partner_id) : comerciais).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Link to="/dashboard/relatorios">
                <Button className="h-9 bg-[#2D3E6F] hover:bg-[#24325c] text-white" data-testid="open-reports-btn">
                  <TrendingUp className="w-4 h-4 mr-2" /> Relatórios
                </Button>
              </Link>
            </div>
          )}
        </div>

        <ChamadasHojeCard />
        <KbNewsBanner />
        <MinhaMetaCard />
        <AgendaHojeCard />

        {/* Contratos Divergentes (NIF) — apenas Gestor/Super Admin */}
        {isFinanceRole && <DivergentContractsCard />}

        {/* Alerts Banner — cards individuais por estado que exige atenção */}
        {alerts?.has_alerts && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="alerts-section">
            {[
              { key: 'nao_atende', label: 'Não Atende', count: alerts.nao_atende, classes: 'bg-rose-50 border-rose-200 hover:border-rose-300', textClasses: 'text-rose-700', iconClasses: 'text-rose-600', arrowClasses: 'text-rose-500', testid: 'alert-nao-atende' },
              { key: 'esclarecimentos', label: 'Esclarecimentos', count: alerts.esclarecimentos, classes: 'bg-amber-50 border-amber-200 hover:border-amber-300', textClasses: 'text-amber-700', iconClasses: 'text-amber-600', arrowClasses: 'text-amber-500', testid: 'alert-esclarecimentos' },
              { key: 'esclarecimentos_respondidos', label: 'Esclar. a Validar', count: alerts.esclarecimentos_respondidos, classes: 'bg-sky-50 border-sky-200 hover:border-sky-300', textClasses: 'text-sky-700', iconClasses: 'text-sky-600', arrowClasses: 'text-sky-500', testid: 'alert-esclarecimentos-respondidos' },
              { key: 'pendente_documentos', label: 'Pendente Documentos', count: alerts.pendente_documentos, classes: 'bg-yellow-50 border-yellow-200 hover:border-yellow-300', textClasses: 'text-yellow-700', iconClasses: 'text-yellow-600', arrowClasses: 'text-yellow-500', testid: 'alert-pendente-documentos' },
              { key: 'anulado_comercializadora', label: 'Anulado Comerc.', count: alerts.anulado_comercializadora, classes: 'bg-orange-50 border-orange-200 hover:border-orange-300', textClasses: 'text-orange-700', iconClasses: 'text-orange-600', arrowClasses: 'text-orange-500', testid: 'alert-anulado-comerc' },
              { key: 'anulado', label: 'Anulado', count: alerts.anulado, classes: 'bg-red-50 border-red-200 hover:border-red-300', textClasses: 'text-red-700', iconClasses: 'text-red-600', arrowClasses: 'text-red-500', testid: 'alert-anulado' },
            ].filter(c => c.count > 0).map(c => {
              // For esclarecimentos_respondidos, show "há X dias" since the OLDEST pending response.
              let ageLabel = null;
              if (c.key === 'esclarecimentos_respondidos' && alerts.esclarecimentos_respondidos_oldest_at) {
                const oldest = new Date(alerts.esclarecimentos_respondidos_oldest_at);
                const days = Math.max(0, Math.floor((Date.now() - oldest.getTime()) / 86400000));
                if (days === 0) ageLabel = 'há < 1d';
                else if (days === 1) ageLabel = 'há 1 dia';
                else ageLabel = `há ${days} dias`;
              }
              return (
              <Link
                key={c.key}
                to={`/contracts/all?status=${c.key}`}
                data-testid={c.testid}
                className={`flex items-center gap-3 p-4 border rounded-xl hover:shadow-sm transition ${c.classes}`}
              >
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${c.iconClasses}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-2xl font-bold leading-tight ${c.textClasses}`}>{c.count}</p>
                  <p className={`text-xs font-medium truncate ${c.textClasses}`}>{c.label}</p>
                  {ageLabel && (
                    <p className={`text-[10px] ${c.textClasses} opacity-80 mt-0.5`} data-testid={`${c.testid}-age`}>
                      mais antigo {ageLabel}
                    </p>
                  )}
                </div>
                <ArrowRight className={`w-4 h-4 ${c.arrowClasses}`} />
              </Link>
            );})}
          </div>
        )}

        {/* Leads para contactar (atrasadas + hoje + amanhã) */}
        {leadsReminder.total > 0 && (
          <Link
            to="/leads/pipeline"
            className={`block rounded-2xl border p-5 transition hover:shadow-md ${
              leadsReminder.atrasadas > 0
                ? 'bg-rose-50 border-rose-200 hover:border-rose-300'
                : leadsReminder.hoje > 0
                  ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
                  : 'bg-sky-50 border-sky-200 hover:border-sky-300'
            }`}
            data-testid="leads-reminder-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  leadsReminder.atrasadas > 0 ? 'bg-rose-100' : leadsReminder.hoje > 0 ? 'bg-amber-100' : 'bg-sky-100'
                }`}>
                  <Phone className={`w-5 h-5 ${
                    leadsReminder.atrasadas > 0 ? 'text-rose-600' : leadsReminder.hoje > 0 ? 'text-amber-600' : 'text-sky-600'
                  }`} />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${
                    leadsReminder.atrasadas > 0 ? 'text-rose-800' : leadsReminder.hoje > 0 ? 'text-amber-800' : 'text-sky-800'
                  }`}>
                    Leads para contactar
                  </p>
                  <div className="flex items-center gap-3 text-xs mt-0.5">
                    {leadsReminder.atrasadas > 0 && (
                      <span className="text-rose-700"><strong>{leadsReminder.atrasadas}</strong> atrasada{leadsReminder.atrasadas !== 1 ? 's' : ''}</span>
                    )}
                    {leadsReminder.hoje > 0 && (
                      <span className="text-amber-700"><strong>{leadsReminder.hoje}</strong> hoje</span>
                    )}
                    {leadsReminder.amanha > 0 && (
                      <span className="text-sky-700"><strong>{leadsReminder.amanha}</strong> amanhã</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-3xl font-bold ${
                  leadsReminder.atrasadas > 0 ? 'text-rose-600' : leadsReminder.hoje > 0 ? 'text-amber-600' : 'text-sky-600'
                }`}>{leadsReminder.total}</span>
                <ArrowRight className={`w-5 h-5 ${
                  leadsReminder.atrasadas > 0 ? 'text-rose-500' : leadsReminder.hoje > 0 ? 'text-amber-500' : 'text-sky-500'
                }`} />
              </div>
            </div>
          </Link>
        )}

        {/* Próximos eventos de calendário (8h) */}
        <UpcomingEventsWidget />

        {/* Follow-ups que precisam de atenção (Top 5) */}
        {urgentFollowUps.length > 0 && (
          <Card className="rounded-2xl border-slate-100" data-testid="urgent-followups-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-[#2D3E6F]" />
                Follow-ups que precisam de mim
              </CardTitle>
              <Link
                to="/leads/follow-ups-fatura"
                className="text-xs font-medium text-[#2D3E6F] hover:underline flex items-center gap-1"
                data-testid="urgent-followups-see-all"
              >
                Ver todos <ArrowRight className="w-3 h-3" />
              </Link>
            </CardHeader>
            <CardContent className="space-y-2">
              {urgentFollowUps.map((f, idx) => {
                const isRed = f.is_red;
                const isDue = f.is_due;
                const stateLabel = isRed ? 'Atrasado' : isDue ? 'A verificar' : 'Próximo';
                const stateClasses = isRed
                  ? 'bg-rose-50 border-rose-200 hover:border-rose-300'
                  : isDue
                  ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
                  : 'bg-slate-50 border-slate-200 hover:border-slate-300';
                const labelClasses = isRed
                  ? 'bg-rose-100 text-rose-700 border-rose-200'
                  : isDue
                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                  : 'bg-slate-100 text-slate-700 border-slate-200';
                return (
                  <Link
                    key={f.id}
                    to="/leads/follow-ups-fatura"
                    data-testid={`urgent-followup-${idx}`}
                    className={`flex items-center justify-between gap-3 p-3 border rounded-xl transition ${stateClasses}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {isRed ? (
                        <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                      ) : (
                        <CalendarClock className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{f.client_name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          #{f.contract_codigo} · prazo {f.scheduled_at ? format(parseISO(f.scheduled_at), "dd MMM yyyy", { locale: pt }) : '—'}
                        </p>
                        <p className="text-xs text-slate-400 truncate flex items-center gap-1.5">
                          <UserCircle className="w-3 h-3 flex-shrink-0" />
                          {f.assigned_to_name || 'Sem comercial'}
                          {f.partner_name && <span className="text-slate-300">·</span>}
                          {f.partner_name && <span className="truncate">{f.partner_name}</span>}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${labelClasses}`}>
                      {stateLabel}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </Link>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Finance Summary - Only for gestor/super_admin */}
        {isFinanceRole && financeSummary && financeSummary.months?.length > 0 && (
          <div className="space-y-4" data-testid="finance-summary-section">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-700 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#2D3E6F]" />
                Resumo Financeiro (3 Meses)
              </h2>
              <Link to="/finance" className="text-xs font-medium text-[#2D3E6F] hover:underline flex items-center gap-1">
                Ver detalhe <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            
            {/* Finance KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {(() => {
                const curr = financeSummary.months[financeSummary.months.length - 1];
                const trends = financeSummary.trends;
                return (
                  <>
                    <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-emerald-100/50">
                      <CardContent className="p-4">
                        <p className="text-xs text-emerald-600 font-medium">Receita ({curr.label})</p>
                        <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="finance-receita">
                          {curr.receita.toLocaleString('pt-PT', { minimumFractionDigits: 0 })}€
                        </p>
                        <div className={`flex items-center gap-1 mt-1 text-xs ${trends.receita >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {trends.receita >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {trends.receita > 0 ? '+' : ''}{trends.receita}% vs mes anterior
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100/50">
                      <CardContent className="p-4">
                        <p className="text-xs text-blue-600 font-medium">Lucro ({curr.label})</p>
                        <p className="text-2xl font-bold text-blue-700 mt-1" data-testid="finance-lucro">
                          {curr.lucro.toLocaleString('pt-PT', { minimumFractionDigits: 0 })}€
                        </p>
                        <div className={`flex items-center gap-1 mt-1 text-xs ${trends.lucro >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {trends.lucro >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {trends.lucro > 0 ? '+' : ''}{trends.lucro}% vs mes anterior
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100/50">
                      <CardContent className="p-4">
                        <p className="text-xs text-purple-600 font-medium">Contratos ({curr.label})</p>
                        <p className="text-2xl font-bold text-purple-700 mt-1" data-testid="finance-contratos">
                          {curr.total_contracts}
                        </p>
                        <div className={`flex items-center gap-1 mt-1 text-xs ${trends.contratos >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {trends.contratos >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {trends.contratos > 0 ? '+' : ''}{trends.contratos}% vs mes anterior
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className={`border-0 shadow-sm ${curr.taxa_chargeback > 5 ? 'bg-gradient-to-br from-red-50 to-red-100/50' : 'bg-gradient-to-br from-slate-50 to-slate-100/50'}`}>
                      <CardContent className="p-4">
                        <p className={`text-xs font-medium ${curr.taxa_chargeback > 5 ? 'text-red-600' : 'text-slate-600'}`}>Chargebacks ({curr.label})</p>
                        <p className={`text-2xl font-bold mt-1 ${curr.taxa_chargeback > 5 ? 'text-red-700' : 'text-slate-700'}`} data-testid="finance-chargebacks">
                          {curr.chargebacks} <span className="text-sm font-normal">({curr.taxa_chargeback}%)</span>
                        </p>
                        <div className={`flex items-center gap-1 mt-1 text-xs ${trends.chargebacks <= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {trends.chargebacks <= 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                          {trends.chargebacks > 0 ? '+' : ''}{trends.chargebacks}% vs mes anterior
                        </div>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Bar Chart - Revenue Breakdown */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-600">Receita vs Lucro (3 Meses)</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={financeSummary.months} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `${v}€`} />
                      <Tooltip
                        formatter={(value) => [`${value.toLocaleString('pt-PT')}€`]}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                      <Bar dataKey="receita" name="Receita" fill="#2D3E6F" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="lucro" name="Lucro" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Line Chart - Trend */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-600">Tendencia de Comissoes (3 Meses)</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={financeSummary.months}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `${v}€`} />
                      <Tooltip
                        formatter={(value) => [`${value.toLocaleString('pt-PT')}€`]}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                      <Line type="monotone" dataKey="comissao_comercial" name="Com. Comercial" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="comissao_parceiro" name="Com. Parceiro" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="bonus_chefe" name="Bonus Chefe" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Oportunidades alert */}
        <OportunidadesCard />

        {/* Main KPIs (clicáveis → Contratos Geral filtrado) */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="kpi-cards">
          <Link to="/contracts/all" className="block">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-[#2D3E6F] to-[#4158A6] text-white hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/70">Total Contratos</p>
                  <p className="text-3xl font-bold mt-1" data-testid="kpi-total">{total_contracts}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-white/30" />
              </div>
            </CardContent>
          </Card>
          </Link>

          <Link to="/contracts/all?status=concluido" className="block">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Concluídos</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1" data-testid="kpi-concluidos">{concluidos}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-100" />
              </div>
            </CardContent>
          </Card>
          </Link>

          <Link to="/contracts/all?status=em_processo" className="block">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Em Processo</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1" data-testid="kpi-emprocesso">{emProcesso}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-100" />
              </div>
            </CardContent>
          </Card>
          </Link>

          <Link to="/contracts/all?status=pendente" className="block">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Pendentes</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="kpi-pendentes">{pendentes}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-100" />
              </div>
            </CardContent>
          </Card>
          </Link>

          <Link to="/contracts/all?status=anulado" className="block">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Anulados</p>
                  <p className="text-3xl font-bold text-red-600 mt-1" data-testid="kpi-anulados">{anulados}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-100" />
              </div>
            </CardContent>
          </Card>
          </Link>

          <Link to="/users" className="block">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Equipa</p>
                  <p className="text-3xl font-bold text-slate-700 mt-1" data-testid="kpi-equipa">{team_members}</p>
                </div>
                <Users className="w-8 h-8 text-slate-200" />
              </div>
            </CardContent>
          </Card>
          </Link>
        </div>

        {/* Service Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Contracts by Category */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-700">Contratos por Serviço</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                const count = contracts_by_category?.[key] || 0;
                const pct = total_contracts > 0 ? (count / total_contracts * 100) : 0;
                const Icon = cfg.icon;
                return (
                  <div key={key} className="flex items-center gap-3" data-testid={`category-${key}`}>
                    <div className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700">{cfg.label}</span>
                        <span className="text-sm font-bold text-slate-800">{count}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${cfg.bg.replace('50', '400')} bg-opacity-70`} style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: pct > 0 ? undefined : 'transparent' }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Contracts by Company */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-700">Contratos por Empresa</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(contracts_by_company || {}).sort((a, b) => b[1] - a[1]).map(([company, count]) => (
                  <div key={company} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg" data-testid={`company-${company}`}>
                    <span className="text-sm text-slate-600 capitalize">{company}</span>
                    <span className="text-sm font-bold text-slate-800">{count}</span>
                  </div>
                ))}
                {Object.keys(contracts_by_company || {}).length === 0 && (
                  <p className="text-sm text-slate-400 col-span-2 text-center py-4">Sem dados</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Service Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Energy Breakdown */}
          <Card className="border-0 shadow-sm" data-testid="energy-breakdown">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                Energia - Detalhe
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <span className="text-sm text-slate-600">Total Energia</span>
                <span className="text-lg font-bold text-slate-800">{energy_by_type?.total || 0}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                  <Zap className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="text-xl font-bold text-yellow-600">{energy_by_type?.luz || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Luz</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                  <p className="text-xl font-bold text-orange-600">{energy_by_type?.gas || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Gás</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <div className="flex items-center justify-center gap-0.5 mb-1">
                    <Zap className="w-4 h-4 text-yellow-500" />
                    <Flame className="w-4 h-4 text-orange-500" />
                  </div>
                  <p className="text-xl font-bold text-emerald-600">{energy_by_type?.luz_gas || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Luz + Gás</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Telecom Breakdown */}
          <Card className="border-0 shadow-sm" data-testid="telecom-breakdown">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
                <Tv className="w-4 h-4 text-purple-500" />
                Telecomunicações - Detalhe
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <span className="text-sm text-slate-600">Total Telecom</span>
                <span className="text-lg font-bold text-slate-800">{telecom_by_type?.total || 0}</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {['1P', '2P', '3P', '4P', '5P'].map(tp => (
                  <div key={tp} className="text-center p-2.5 bg-slate-50 rounded-lg">
                    <p className="text-lg font-bold text-purple-600">{telecom_by_type?.[tp] || 0}</p>
                    <p className="text-xs text-slate-500">{tp}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Solar Stats */}
          <Card className="border-0 shadow-sm" data-testid="solar-breakdown">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
                <Sun className="w-4 h-4 text-amber-500" />
                Solar - Detalhe
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <span className="text-sm text-slate-600">Total Solar</span>
                <span className="text-lg font-bold text-slate-800">{solar_stats?.total || 0}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-xl font-bold text-emerald-600">{solar_stats?.concluidos || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Concluídos</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-xl font-bold text-amber-600">{solar_stats?.total_paineis || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Painéis Vendidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row: Recent Activity & Tickets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Activities */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-700">Atividade Recente</CardTitle>
            </CardHeader>
            <CardContent>
              {recent_activities?.length > 0 ? (
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {recent_activities.slice(0, 10).map(activity => (
                    <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-slate-50 last:border-0">
                      <div className="w-8 h-8 rounded-full bg-[#2D3E6F]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FileText className="w-3.5 h-3.5 text-[#2D3E6F]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-700 line-clamp-2">{activity.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">{activity.created_by_name}</span>
                          {activity.created_at && (
                            <span className="text-xs text-slate-400">
                              {format(parseISO(activity.created_at), "dd MMM HH:mm", { locale: pt })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-6">Sem atividade recente</p>
              )}
            </CardContent>
          </Card>

          {/* My Tickets */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-700">Os Meus Tickets</CardTitle>
              <Link to="/tickets" className="text-xs text-[#2D3E6F] hover:underline">Ver todos</Link>
            </CardHeader>
            <CardContent>
              {my_tickets?.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {my_tickets.map(ticket => (
                    <div key={ticket.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate">{ticket.description || ticket.tipo}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{ticket.contract_nome_cliente || '-'}</p>
                      </div>
                      <StatusBadge status={ticket.status} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-6">Sem tickets</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Status Breakdown */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-700">Contratos por Estado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(contracts_by_status || {}).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                <div key={status} className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-800">{count}</p>
                  <p className="text-xs text-slate-500 mt-1">{STATUS_LABELS[status] || status}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
